import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '@/lib/firebase';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { router } from '@/router';
import { pick } from 'lodash';
import { useSideDrawer } from '@/hooks/state/useSideDrawer';
import { useScribeWindow } from '@/hooks/state/useScribeWindow';
import { clearAllOttoPilotDrafts } from '@/hooks/ottoPilotDraftStorage';

interface User {
  uid: string;
  displayName: string | null;
  email: string | null;
}

export interface AuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  isImpersonating: boolean;
  awaitAuthState: () => Promise<AuthState>;
  loginWithEmailAndPassword: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<void>;
  loginWithCustomToken: ({ token }: { token: string }) => Promise<void>;
  logout: () => void;
  exitImpersonation: () => void;
}

type AuthState = Pick<AuthContext, 'isAuthenticated' | 'isLoading' | 'user' | 'isImpersonating'>;

const AuthContext = createContext<AuthContext | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During hot reloads, the context might not be available immediately
    // Return a default context to prevent crashes, but log a warning
    if (process.env.NODE_ENV === 'development') {
      console.warn('useAuth called outside AuthProvider - this may happen during hot reloads');
      // Return a minimal context to prevent crashes during hot reload
      window.location.reload();
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    isImpersonating: false,
  });
  const { isAuthenticated, isLoading, user, isImpersonating } = authState;
  const { resetDrawer } = useSideDrawer();
  const { closeScribeWindow } = useScribeWindow();

  const loginWithEmailAndPassword = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithCustomToken = async ({ token }: { token: string }) => {
    const userCredential = await signInWithCustomToken(auth, token);
    const user = userCredential.user;

    // Read impersonation state from token claims
    const idTokenResult = await user.getIdTokenResult();
    const isImpersonatingFromToken = idTokenResult.claims.isImpersonating === true;

    setAuthState({
      isAuthenticated: true,
      isLoading: false,
      user,
      isImpersonating: isImpersonatingFromToken,
    });
  };

  const logout = async () => {
    clearAllOttoPilotDrafts();
    await signOut(auth);
  };

  const exitImpersonation = () => {
    clearAllOttoPilotDrafts();
    signOut(auth);
    router.navigate({ to: '/login' });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      resetDrawer();
      closeScribeWindow();
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const impersonating = idTokenResult.claims.isImpersonating === true;
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: pick(user, ['uid', 'displayName', 'email']),
          isImpersonating: impersonating,
        });
      } else {
        clearAllOttoPilotDrafts();
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          isImpersonating: false,
        });
      }
      router.invalidate();
    });
    return () => unsubscribe();
  }, [closeScribeWindow, resetDrawer]);

  const awaitAuthState = async (): Promise<AuthState> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        let authState: AuthState;

        if (user) {
          // Read impersonation state from token claims
          const idTokenResult = await user.getIdTokenResult();
          const impersonating = idTokenResult.claims.isImpersonating === true;

          authState = {
            isAuthenticated: true,
            isLoading: false,
            user,
            isImpersonating: impersonating,
          };
        } else {
          authState = {
            isAuthenticated: false,
            isLoading: false,
            user: null,
            isImpersonating: false,
          };
        }
        resolve(authState);
      });
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        isImpersonating,
        awaitAuthState,
        loginWithEmailAndPassword,
        loginWithCustomToken,
        logout,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
